import React from 'react';
import type { Socket } from 'socket.io-client';

import { useAuth } from '../context/AuthContext';
import { useDevices } from '../context/DeviceContext';
import { useGeofenceAreas } from '../context/GeofenceAreaContext';
import { useLog } from '../context/LogContext';
import { useMap } from '../context/MapContext';
import type { Device, LogEntry } from '../types';
import { formatDateAsAge } from '../utils/dateUtils';
import { isBatteryLow, showAsActive } from '../utils/deviceUtils';

import { List } from './common/List';
import { MaintenanceButton } from './common/MaintenanceButton';
import { InfoBox } from './device/InfoBox';
import { SearchBox } from './filters/SearchBox';
import { TimeFilter, TimeFilterBox } from './filters/TimeFilter';
import { Map, Trace } from './map';
import { getAreaBounds } from './map/GeofenceAreas/shapeBuilders';

interface MapViewProps {
  socket: Socket | null;
  setView: (view: string) => void;
  handleLogout: () => void;
  /** Incremented each time the admin requests opening the geofence area editor */
  openAreaEditorRequest?: number;
}

/**
 * MapView Component - Main application view with map and device management.
 * Renders the map, filters, device/log/user lists, and trace view.
 */
export const MapView: React.FC<MapViewProps> = ({
  socket,
  setView,
  handleLogout,
  openAreaEditorRequest,
}) => {
  const { showFilters, isAdmin } = useAuth();
  const { log } = useLog();
  const {
    devices,
    filteredDevices,
    selectedDevice,
    searchQuery,
    setSearchQuery,
    alarmFilter,
    setAlarmFilter,
    positionFilter,
    setPositionFilter,
    signalFilter,
    setSignalFilter,
    selectDevice,
    currentCheckpoint,
    geofenceFitboundsPadding,
  } = useDevices();
  const {
    projection,
    zoom,
    setZoom,
    center,
    waitForPos,
    isTraceViewOpen,
    currentTrace,
    tracedDevice,
    closeTraceView,
    centerMapOn,
    requestFitBounds,
  } = useMap();

  // Shared geofence area configs (fetched once on auth). `loaded` lets the map hold
  // beacon/checkin markers until the suppression set is known, avoiding the fresh-login
  // flash of geofenced infrastructure as scattered point markers.
  const { areaConfigs, devicesWithAreaShape, loaded: areaConfigsLoaded } = useGeofenceAreas();

  const handleCloseTraceView = (): void => {
    closeTraceView();
    selectDevice(null);
  };

  // Calculate some helper functions for filters
  const now = Date.now() / 1000;

  const calculateAlarmAge = (device: Device): number => {
    if (!device.alarmTime) return -1;
    return (now - new Date(device.alarmTime).getTime() / 1000) / 60;
  };

  const calculatePositionAge = (device: Device): number => {
    if (!device.positionTime) return -1;
    return (now - new Date(device.positionTime).getTime() / 1000) / 60;
  };

  const calculateSignalAge = (device: Device): number => {
    if (!device.timestamp) return -1;
    return (now - new Date(device.timestamp).getTime() / 1000) / 60;
  };

  // Render trace view if open
  if (isTraceViewOpen) {
    return (
      <div>
        <Trace
          trace={currentTrace ?? undefined}
          projection={projection}
          zoom={zoom}
          setZoom={setZoom}
          selectedDevice={tracedDevice}
          onClose={handleCloseTraceView}
        />

        {/* Position History is now shown inside the Trace component */}

        {/* Maintenance Button - Admin and above - in trace view */}
        {isAdmin && <MaintenanceButton onClick={() => setView('maintenance')} socket={socket} />}
      </div>
    );
  }

  // Render main map view
  return (
    <div>
      {/* Main Map Component */}
      <Map
        projection={projection}
        center={center ?? undefined}
        zoom={zoom}
        setZoom={setZoom}
        waitForPos={waitForPos}
        onLogout={handleLogout}
        devicesWithAreaShape={devicesWithAreaShape}
        areaConfigsLoaded={areaConfigsLoaded}
        openAreaEditorRequest={openAreaEditorRequest}
      />

      {/* Device Information Box */}
      <InfoBox
        devices={devices}
        filteredDevices={filteredDevices}
        currentCheckpoint={currentCheckpoint}
      />

      {/* Search Box */}
      {showFilters && (
        <SearchBox
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          spaceForFilters={showFilters}
        />
      )}

      {/* Time Filters */}
      {showFilters && (
        <>
          <TimeFilter
            position={0}
            text="Max Alarm Age"
            getValue={() => alarmFilter}
            setValue={setAlarmFilter}
          />

          <TimeFilter
            position={1}
            text="Max Position Age"
            getValue={() => positionFilter}
            setValue={setPositionFilter}
          />

          <TimeFilter
            position={2}
            text="Max Signal Age"
            getValue={() => signalFilter}
            setValue={setSignalFilter}
          />
        </>
      )}

      {/* Filter Boxes */}
      {showFilters && (
        <>
          <TimeFilterBox
            position={0}
            devices={devices}
            predicate={(device) => {
              const alarmAge = calculateAlarmAge(device);
              return alarmAge > 0 && (alarmFilter === 0 || alarmAge <= alarmFilter / 60);
            }}
            getAge={calculateAlarmAge}
            text="Alarms"
            getValue={() => selectedDevice}
            setValue={selectDevice}
          />

          <TimeFilterBox
            position={1}
            devices={devices}
            predicate={(device) => {
              const positionAge = calculatePositionAge(device);
              return Boolean(positionFilter) && positionAge > positionFilter / 60;
            }}
            getAge={calculatePositionAge}
            text="Unpositioned"
            getValue={() => selectedDevice}
            setValue={selectDevice}
          />

          <TimeFilterBox
            position={2}
            devices={devices}
            predicate={(device) => {
              const signalAge = calculateSignalAge(device);
              return Boolean(signalFilter) && signalAge > signalFilter / 60;
            }}
            getAge={calculateSignalAge}
            text="Offline"
            getValue={() => selectedDevice}
            setValue={selectDevice}
          />
        </>
      )}

      {/* Device List - Only for admin users */}
      {isAdmin && (
        <List<Device>
          key="devices"
          icon="☷"
          items={devices.filter((d) => !d.deviceCategory || d.deviceCategory === 'lf_device')}
          text="Devices"
          left={true}
          headers={['Title', 'Signal Age', 'Position Age', 'Alarm Age']}
          format={(device) => ({
            title: (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4em' }}>
                {/* Active/inactive indicator — green when device reports deviceActive=1 */}
                <span
                  title={showAsActive(device) ? 'Aktiv' : undefined}
                  style={{
                    width: '0.7em',
                    height: '0.7em',
                    minWidth: 7,
                    minHeight: 7,
                    borderRadius: '50%',
                    background: showAsActive(device) ? '#1a9' : '#bbb',
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                />
                {(device.title ?? '') +
                  (isBatteryLow(device)
                    ? ' 🪫 (' + Math.round((device.output?.volts ?? 0) * 10) / 10 + 'V)'
                    : '')}
              </span>
            ),
            timestamp: device.timestamp ? formatDateAsAge(new Date(device.timestamp)) : '',
            positioned: device.positionTime ? formatDateAsAge(new Date(device.positionTime)) : '',
            alarm: device.alarmTime ? formatDateAsAge(new Date(device.alarmTime)) : '',
          })}
          select={(device) => {
            selectDevice(device ? device.id : '');
            if (!device?.latlng) return;
            if (device.positionType === 'GEOFENCE' && device.area) {
              const area = areaConfigs.find((a) => a.area_name === device.area && a.area_shape);
              if (area?.area_shape) {
                requestFitBounds(getAreaBounds(area.area_shape), geofenceFitboundsPadding);
                return;
              }
            }
            centerMapOn(device.latlng);
            if (zoom < 16) setZoom(16);
          }}
          isSelected={(device) => selectedDevice === device.id}
        />
      )}

      {/* Log List - Only for admin users */}
      {isAdmin && (
        <List<LogEntry>
          key="log"
          icon="☰"
          items={log}
          text="Log"
          left={false}
          headers={['Log Timestamp', 'Actor', 'Event']}
          format={(item) => ({
            timestamp: new Date(item.timestamp).toLocaleString(),
            user: item.username ?? '',
            text: item.text ?? '',
          })}
          select={(item) => selectDevice(item ? (item.device ?? '') : '')}
          isSelected={(item) => selectedDevice === item.device}
          sortReversed={true}
        />
      )}

      {/* Maintenance Button - Admin and above */}
      {isAdmin && <MaintenanceButton onClick={() => setView('maintenance')} socket={socket} />}
    </div>
  );
};
